/**
 * Hook: usePipelineTags
 *
 * CRUD de tags reutilizáveis (escopo tenant-wide) referenciadas por id em
 * pipeline_cards.dados_card.tags
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { PipelineTag } from '../types';
import { pipelineTagsKey } from './queryKeys';

const EMPTY_TAGS: PipelineTag[] = [];

function tagErrorMessage(error: { message: string; code?: string }): string {
  if (error.code === '42501' || /row-level security/i.test(error.message)) {
    return '403 Forbidden: você não tem permissão para gerenciar tags deste tenant.';
  }
  if (error.code === '23505') {
    return 'Já existe uma tag com esse nome.';
  }
  return error.message;
}

export function usePipelineTags() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const tenantId = profile?.tenant_id ?? null;

  const { data: tags = EMPTY_TAGS, isLoading, error, refetch } = useQuery({
    queryKey: pipelineTagsKey(tenantId ?? -1),
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error: queryError } = await supabase
        .from('pipeline_tags')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('nome', { ascending: true });
      if (queryError) throw new Error(queryError.message);
      return (data || []) as PipelineTag[];
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });

  const tagsById = useMemo(() => {
    const map = new Map<string, PipelineTag>();
    tags.forEach((t) => map.set(t.id, t));
    return map;
  }, [tags]);

  const invalidate = () => {
    if (!tenantId) return;
    queryClient.invalidateQueries({ queryKey: pipelineTagsKey(tenantId) });
  };

  /**
   * Find-or-create por nome (case-insensitive) — evita duplicatas em
   * corridas (dois usuários criando "vip" ao mesmo tempo): tenta o insert
   * e, se colidir com a constraint única, refaz o select para pegar a tag
   * já existente.
   */
  const createTag = useMutation({
    mutationFn: async (input: { nome: string; cor?: string }): Promise<PipelineTag> => {
      if (!tenantId || !profile?.uuid) throw new Error('Tenant não identificado');
      const nomeTrim = input.nome.trim();
      if (!nomeTrim) throw new Error('Nome da tag não pode ser vazio');

      const existing = tags.find((t) => t.nome.toLowerCase() === nomeTrim.toLowerCase());
      if (existing) return existing;

      const { data, error: insertError } = await supabase
        .from('pipeline_tags')
        .insert({
          tenant_id: tenantId,
          nome: nomeTrim,
          cor: input.cor,
          criado_por: profile.uuid,
        })
        .select()
        .single();

      if (insertError) {
        if (insertError.code === '23505') {
          const { data: found } = await supabase
            .from('pipeline_tags')
            .select('*')
            .eq('tenant_id', tenantId)
            .ilike('nome', nomeTrim)
            .maybeSingle();
          if (found) return found as PipelineTag;
        }
        throw new Error(tagErrorMessage(insertError));
      }
      return data as PipelineTag;
    },
    onSuccess: () => invalidate(),
  });

  /**
   * Renomeia/recolore uma tag. Como o card referencia a tag por id em
   * dados_card.tags, isso propaga automaticamente para todo lead que a usa
   * — nenhum card precisa ser tocado.
   */
  const updateTag = useMutation({
    mutationFn: async (input: { id: string; nome?: string; cor?: string }): Promise<PipelineTag> => {
      const payload: Record<string, string> = {};
      if (input.nome !== undefined) {
        const nomeTrim = input.nome.trim();
        if (!nomeTrim) throw new Error('Nome da tag não pode ser vazio');
        payload.nome = nomeTrim;
      }
      if (input.cor !== undefined) payload.cor = input.cor;

      const { data, error: updateError } = await supabase
        .from('pipeline_tags')
        .update(payload)
        .eq('id', input.id)
        .select()
        .single();

      if (updateError) throw new Error(tagErrorMessage(updateError));
      return data as PipelineTag;
    },
    onSuccess: () => invalidate(),
  });

  const deleteTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error: deleteError } = await supabase
        .from('pipeline_tags')
        .delete()
        .eq('id', tagId);
      if (deleteError) throw new Error(tagErrorMessage(deleteError));
      return tagId;
    },
    onSuccess: () => invalidate(),
  });

  return {
    tags,
    tagsById,
    loading: isLoading,
    error: error as Error | null,
    refetch,
    createTag,
    updateTag,
    deleteTag,
  };
}
